import { useEffect, useState } from 'react';
import { Input } from '../../../components/ui';

interface SlugInputProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    sourceText?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
}

/**
 * SlugInput component - Auto-generates slug from source text but allows manual editing
 */
export default function SlugInput({
    label,
    value,
    onChange,
    sourceText,
    placeholder = 'auto-generated-slug',
    disabled = false,
    required = false,
}: SlugInputProps) {
    const [isManuallyEdited, setIsManuallyEdited] = useState(false);

    // Generate slug from source text
    const generateSlug = (text: string): string => {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single
            .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    };

    // Auto-generate slug when source text changes (only if not manually edited)
    useEffect(() => {
        if (sourceText && !isManuallyEdited) {
            const newSlug = generateSlug(sourceText);
            onChange(newSlug);
        }
    }, [sourceText, isManuallyEdited, onChange]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsManuallyEdited(true);
        const slug = generateSlug(e.target.value);
        onChange(slug);
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-[#1e293b]">
                {label}
                {required && <span className="text-[#ef4444] ml-1">*</span>}
            </label>
            <div className="relative">
                <Input
                    type="text"
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="font-mono text-sm"
                />
                {!isManuallyEdited && sourceText && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <span className="text-xs text-[#64748b] bg-[#f1f5f9] px-2 py-1 rounded">
                            Auto
                        </span>
                    </div>
                )}
            </div>
            <p className="text-xs text-[#64748b]">
                {isManuallyEdited
                    ? 'Manually edited. Click to continue editing.'
                    : 'Auto-generated from name. You can edit if needed.'}
            </p>
        </div>
    );
}
